import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ArchiveIcon from '@mui/icons-material/Archive';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import GroupsIcon from '@mui/icons-material/Groups';
import HelpIcon from '@mui/icons-material/Help';
import HomeIcon from '@mui/icons-material/Home';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PersonIcon from '@mui/icons-material/Person';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import TimelineIcon from '@mui/icons-material/Timeline';
import { Badge, Box, ListItemText, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import type { Notification } from './services/notificationService';
import { PERMISSIONS } from './shared/constants/permissions';

interface StyledIconProps {
  children: React.ReactElement;
  className?: string;
  sx?: object;
}

interface User {
  role?: string;
  cachedPermissions?: string[];
  teamId?: string;
  subscription?: {
    status?: string;
  } | null;
  clientSettings?: {
    [key: string]: unknown;
    enableBiddingModule?: boolean;
    enableProcurementModule?: boolean;
  };
}

interface NavigationItem {
  segment?: string;
  pattern?: string;
  title?: string | React.ReactElement | string;
  icon?: React.ReactElement;
  kind?: 'divider' | 'header' | 'page';
  indicator?: boolean;
  children?: NavigationItem[];
  defaultExpanded?: boolean;
}

const StyledIcon: React.FC<StyledIconProps> = ({ children, ...props }) => {
  const theme = useTheme();
  return React.cloneElement(children as React.ReactElement<{ sx?: object }>, {
    ...props,
    sx: {
      color: 'inherit',
      '.Mui-selected &': {
        color: theme.palette.bidlogiq.black,
      },
      ...props.sx,
    },
  });
};

const BASE_NAVIGATION: NavigationItem[] = [
  {
    segment: 'dashboard',
    title: 'My Tenders',
    icon: (
      <StyledIcon>
        <HomeIcon />
      </StyledIcon>
    ),
  },
  {
    segment: 'assignments',
    title: 'Assignments',
    icon: (
      <StyledIcon>
        <AssignmentIcon />
      </StyledIcon>
    ),
  },
  {
    segment: 'public-tenders',
    title: 'Public Tenders',
    icon: (
      <StyledIcon>
        <FormatListBulletedIcon />
      </StyledIcon>
    ),
  },
  {
    segment: 'partners',
    title: 'Partners',
    icon: (
      <StyledIcon>
        <GroupsIcon />
      </StyledIcon>
    ),
  },
  {
    segment: 'templates',
    title: 'Templates',
    icon: (
      <StyledIcon>
        <DescriptionIcon />
      </StyledIcon>
    ),
  },
  {
    segment: 'archive',
    title: 'Archive',
    icon: (
      <StyledIcon>
        <ArchiveIcon />
      </StyledIcon>
    ),
  },
];

const SUPPORT_NAVIGATION: NavigationItem[] = [
  {
    kind: 'divider',
  },
  {
    segment: 'account',
    title: 'My Account',
    icon: (
      <StyledIcon>
        <PersonIcon />
      </StyledIcon>
    ),
  },
  {
    segment: 'help',
    title: 'Need Help?',
    icon: (
      <StyledIcon>
        <HelpIcon />
      </StyledIcon>
    ),
  },
];

import type { WorkspaceMode } from './contexts/WorkspaceContext';
import type { Procurement } from './types/procurement';
import type { Tender } from './types/tender';

export const getNavigationForUser = (
  user?: User | null,
  notifications?: Notification[],
  recentTenders?: Tender[],
  recentProcurements?: Procurement[],
  workspaceMode: WorkspaceMode = 'BID'
): NavigationItem[] => {
  const navigation: NavigationItem[] = [];

  const unreadHelpTicketNotifications = Boolean(
    (notifications ?? []).filter(
      (n) => !n.read && typeof n.title === 'string' && n.title.toLowerCase().includes('help ticket')
    ).length > 0
  );

  const getAdminChildren = () => {
    const children: NavigationItem[] = [];
    children.push({
      segment: 'overview',
      title: 'Overview',
      icon: (
        <StyledIcon>
          <DashboardIcon />
        </StyledIcon>
      ),
    });
    if (
      user?.cachedPermissions?.includes(PERMISSIONS.CLIENT_VIEW) ||
      user?.cachedPermissions?.includes(PERMISSIONS.CLIENT_PROFILE_UPDATE)
    ) {
      children.push({
        segment: 'company-info',
        title: 'Company Info',
        icon: (
          <StyledIcon>
            <BusinessIcon />
          </StyledIcon>
        ),
      });
    }
    if (user?.cachedPermissions?.includes(PERMISSIONS.INSIGHTS_VIEW)) {
      children.push({
        segment: 'analytics',
        title: 'Analytics',
        icon: (
          <StyledIcon>
            <DashboardIcon />
          </StyledIcon>
        ),
      });
    }
    if (user?.cachedPermissions?.includes(PERMISSIONS.BILLING_MANAGE)) {
      children.push({
        segment: 'billing',
        title: 'Billing',
        icon: (
          <StyledIcon>
            <AccountBalanceWalletIcon />
          </StyledIcon>
        ),
      });
    }
    if (user?.cachedPermissions?.includes(PERMISSIONS.SYSTEM_MANAGE)) {
      children.push({
        segment: 'platform_metrics',
        title: 'Platform Metrics',
        icon: (
          <StyledIcon>
            <TimelineIcon />
          </StyledIcon>
        ),
      });
    }
    if (user?.cachedPermissions?.includes(PERMISSIONS.TICKET_MANAGE)) {
      children.push({
        segment: 'helpdesk',
        title: 'Helpdesk',
        icon: (
          <StyledIcon>
            <AssignmentIcon />
          </StyledIcon>
        ),
      });
    }
    if (user?.cachedPermissions?.includes(PERMISSIONS.SYSTEM_VIEW_ALL_CLIENTS)) {
      children.push({
        segment: 'clients',
        title: 'Clients',
        icon: (
          <StyledIcon>
            <BusinessIcon />
          </StyledIcon>
        ),
      });
    }
    if (user?.cachedPermissions?.includes(PERMISSIONS.LIBRARY_MANAGE_CLIENT)) {
      children.push({
        segment: 'knowledge_base',
        title: 'Knowledge Base',
        icon: (
          <StyledIcon>
            <LibraryBooksIcon />
          </StyledIcon>
        ),
      });
    }
    if (user?.cachedPermissions?.includes(PERMISSIONS.TEAM_VIEW)) {
      children.push({
        segment: 'teams',
        title: 'Teams',
        icon: (
          <StyledIcon>
            <GroupsIcon />
          </StyledIcon>
        ),
      });
    }
    if (user?.cachedPermissions?.includes(PERMISSIONS.USER_VIEW)) {
      children.push({
        segment: 'users',
        title: 'Users',
        icon: (
          <StyledIcon>
            <PersonIcon />
          </StyledIcon>
        ),
      });
    }
    if (user?.cachedPermissions?.includes(PERMISSIONS.SETTINGS_MANAGE)) {
      children.push({
        segment: 'storage',
        title: 'Storage',
        icon: (
          <StyledIcon>
            <StorageIcon />
          </StyledIcon>
        ),
      });
    }
    if (user?.cachedPermissions?.includes(PERMISSIONS.SYSTEM_MANAGE)) {
      children.push({
        segment: 'pricing',
        title: 'Pricing',
        icon: (
          <StyledIcon>
            <SettingsIcon />
          </StyledIcon>
        ),
      });
    }
    if (user?.cachedPermissions?.includes(PERMISSIONS.SETTINGS_MANAGE)) {
      children.push({
        segment: 'settings',
        title: 'Settings',
        icon: (
          <StyledIcon>
            <SettingsIcon />
          </StyledIcon>
        ),
      });
    }
    return children;
  };

  if (user?.cachedPermissions?.includes(PERMISSIONS.SYSTEM_MANAGE)) {
    // For super admins, essentially replace the existing navbar with just the Admin features.
    // We flatten the admin navigation so it is "expanded" by default as top-level items.
    const expandedAdminNav = getAdminChildren().map((child) => ({
      ...child,
      segment: child.segment === 'company-info' ? child.segment : `admin/${child.segment}`,
    }));
    navigation.push({ kind: 'header', title: 'Admin Settings' });
    navigation.push(...expandedAdminNav);
    return navigation;
  }

  // PROCUREMENT WORKSPACE NAVIGATION
  if (workspaceMode === 'PROCURE') {
    navigation.push({
      segment: 'procure/dashboard',
      title: 'My Procurements',
      icon: (
        <StyledIcon>
          <HomeIcon />
        </StyledIcon>
      ),
    });
    if (recentProcurements && recentProcurements.length > 0) {
      navigation.push({ kind: 'divider' });
      navigation.push({ kind: 'header', title: 'Recent Procurements' });

      const sortedProcurements = [...recentProcurements].sort((a, b) => {
        const titleA = a.title || 'Untitled Procurement';
        const titleB = b.title || 'Untitled Procurement';
        return titleA.localeCompare(titleB);
      });

      for (const proc of sortedProcurements) {
        navigation.push({
          segment: `procure/detail/${proc.id}`,
          pattern: `procure/detail/${proc.id}(/.*)?`,
          title: (
            <Tooltip
              title={proc.title || 'Untitled Procurement'}
              placement="right"
              slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
            >
              <Typography
                color="inherit"
                className="recent-tender-title" // Reuse class for styling
                sx={{
                  display: 'block',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: 1.2,
                  p: 0,
                  m: 0,
                  fontSize: '0.85rem',
                }}
              >
                {proc.title || 'Untitled Procurement'}
              </Typography>
            </Tooltip>
          ),
          icon: (
            <StyledIcon sx={{ opacity: 0.7, transform: 'scale(0.8)' }}>
              <DescriptionOutlinedIcon fontSize="small" />
            </StyledIcon>
          ),
        });
      }
    }

    // navigation.push({
    //   segment: 'procure/archive',
    //   title: 'Archive',
    //   icon: (
    //     <StyledIcon>
    //       <ArchiveIcon />
    //     </StyledIcon>
    //   ),
    // });
  }
  // BIDDING WORKSPACE NAVIGATION (Default)
  else {
    const baseNavigation = BASE_NAVIGATION.filter((item) => {
      if (user?.subscription?.status === 'trialing') {
        if (['assignments', 'partners', 'archive'].includes(item.segment as string)) {
          return false;
        }
      }

      if (item.segment === 'public-tenders') {
        return user?.cachedPermissions?.includes(PERMISSIONS.PUBLIC_TENDER_VIEW);
      }
      if (item.segment === 'partners') {
        return user?.cachedPermissions?.includes(PERMISSIONS.PARTNER_VIEW);
      }
      if (item.segment === 'templates') {
        return (
          user?.cachedPermissions?.includes(PERMISSIONS.TEMPLATE_UPLOAD_TEAM) ||
          user?.cachedPermissions?.includes(PERMISSIONS.TEMPLATE_MANAGE_CLIENT)
        );
      }
      if (item.segment === 'insights') {
        return user?.cachedPermissions?.includes(PERMISSIONS.INSIGHTS_VIEW);
      }
      if (item.segment === 'company-info') {
        return (
          user?.cachedPermissions?.includes(PERMISSIONS.CLIENT_VIEW) ||
          user?.cachedPermissions?.includes(PERMISSIONS.CLIENT_PROFILE_UPDATE)
        );
      }
      return true;
    }).reduce<NavigationItem[]>((acc, item) => {
      acc.push(item);
      if (item.segment === 'dashboard') {
        if (recentTenders && recentTenders.length > 0) {
          acc.push({ kind: 'divider' });
          acc.push({ kind: 'header', title: 'Recent Tenders' });

          const sortedTenders = [...recentTenders].sort((a, b) => {
            const titleA = a.title || 'Untitled Tender';
            const titleB = b.title || 'Untitled Tender';
            return titleA.localeCompare(titleB);
          });

          for (const tender of sortedTenders) {
            const isWatching = tender.pursuitStatus === 'watching';
            const isPursuing = tender.pursuitStatus === 'pursuing';

            acc.push({
              segment: `tender/${tender.id}`,
              pattern: `tender/${tender.id}(/.*)?`,
              title: (
                <Tooltip
                  title={tender.title || 'Untitled Tender'}
                  placement="right"
                  slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
                >
                  <Typography
                    color="inherit"
                    className="recent-tender-title"
                    sx={{
                      display: 'block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.2,
                      p: 0,
                      m: 0,
                      fontSize: '0.85rem',
                    }}
                  >
                    {' '}
                    {tender.title || 'Untitled Tender'}
                  </Typography>
                </Tooltip>
              ),
              icon: (
                <StyledIcon>
                  {isPursuing ? (
                    <CheckCircleIcon fontSize="small" />
                  ) : isWatching ? (
                    <RadioButtonUncheckedIcon fontSize="small" />
                  ) : (
                    <DescriptionOutlinedIcon fontSize="small" />
                  )}
                </StyledIcon>
              ),
            });
          }
          acc.push({ kind: 'divider' });
        }

        if (
          (user?.role === 'team_lead' || user?.role === 'client_admin') &&
          user?.teamId &&
          user?.subscription?.status !== 'trialing'
        ) {
          acc.push({
            segment: `admin/team/${user.teamId}`,
            title: 'My Team',
            icon: (
              <StyledIcon>
                <GroupsIcon />
              </StyledIcon>
            ),
          });
        }
      }
      return acc;
    }, []);

    navigation.push(...baseNavigation);
  }

  const hasAdminAccess =
    (user?.cachedPermissions &&
      [
        PERMISSIONS.SYSTEM_MANAGE,
        PERMISSIONS.CLIENT_PROFILE_UPDATE,
        PERMISSIONS.TEAM_VIEW,
        PERMISSIONS.USER_VIEW,
        PERMISSIONS.BILLING_MANAGE,
        PERMISSIONS.SETTINGS_MANAGE,
        PERMISSIONS.TICKET_MANAGE,
        PERMISSIONS.INSIGHTS_VIEW,
      ].some((p) => user.cachedPermissions?.includes(p))) ||
    false;

  const supportNavigation: NavigationItem[] = SUPPORT_NAVIGATION.map((item) => {
    if (item.segment === 'help') {
      return {
        ...item,
        title: (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ListItemText primary="Need Help?" />
            {unreadHelpTicketNotifications && <Badge color="error" variant="dot" sx={{ ml: 1 }} />}
          </Box>
        ),
      };
    }
    return item;
  });

  navigation.push(...supportNavigation);

  if (hasAdminAccess) {
    const expandedAdminNav = getAdminChildren().map((child) => ({
      ...child,
      segment: child.segment === 'company-info' ? child.segment : `admin/${child.segment}`,
    }));
    navigation.push({ kind: 'divider' });
    navigation.push({ kind: 'header', title: 'Admin Settings' });
    navigation.push(...expandedAdminNav);
  }

  return navigation;
};

export const NAVIGATION: NavigationItem[] = BASE_NAVIGATION.concat(SUPPORT_NAVIGATION);

interface Branding {
  title: string;
  homeUrl: string;
}

export const BRANDING: Branding = {
  title: '',
  homeUrl: '',
};
